<template>
    <a class="skip-link" href="#content" tabindex="1">
      Skip to content
    </a>
    <header-component v-if="$route.meta.showHeader"></header-component>
    <cookie-bar v-if="showPopup" @close-popup="onClosePopup"/>

    <router-view v-slot="{ Component }">

    

        <template v-if="isSafari()">
            <Component :is="Component" :class=" isTransitioning? 'hide-content' : ''" class="show-content"/>
            <footer-component v-if="$route.meta.showFooter" :class=" isTransitioning? 'hide-content' : ''"/>
        </template>
        <template v-else>
            <transition name="up" mode="out-in" @after-enter="onAfterEnter" @before-leave="onBeforeLeave">
                <Component :is="Component" :class=" isTransitioning? 'hide-content' : ''" class="show-content"/>
            </transition>
            
            <transition name="footer" @before-enter="onFooterBeforeEnter" @after-leave="onFooterAfterLeave">
                <footer-component v-if="$route.meta.showFooter" :class=" isTransitioning? 'hide-content' : ''"/>
            </transition>
        </template>

    </router-view>
</template>

<script lang="ts">

    import { createApp, defineComponent, ref, onUpdated, onMounted, nextTick, watch } from 'vue'
    import router from './routes'
    import { useQuery } from '@vue/apollo-composable'
    import gql from 'graphql-tag'

    import { useUserway } from './composables/useUserway'
    import { setupGlobalErrorHandler } from './composables/useErrorHandler'

    import HeaderComponent from "./components/navigation/Header.vue";
    import FooterComponent from "./components/navigation/Footer.vue";
    import CookieBar from "./components/navigation/CookieBar.vue";
    import { gsap } from "gsap";
    import SplitText from "gsap/SplitText"
    import ScrollTrigger from "gsap/ScrollTrigger"
    import whatInput from 'what-input';

    gsap.registerPlugin(ScrollTrigger, SplitText)

    const hasConsentedToCookie = () => {
        return document.cookie.includes('cookieConsent=true') || document.cookie.includes('cookieConsent=false');
    };


    export default defineComponent({
        components: {
            HeaderComponent,
            FooterComponent,
            CookieBar
        },

        setup() {
            const showPopup = ref(!hasConsentedToCookie());
            const animatedHeadings = ref([]);
            const animatedHeadingsLoaded = ref(false);

            // Initialize UserWay composable
            const { isLoaded: userwayLoaded, config: userwayConfig, error: userwayError, loadUserwayConfig } = useUserway();

            const onClosePopup = () => {
                showPopup.value = false;
            };

            // Safari detection function
            const isSafari = () => {
                const safariDetected = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                if (safariDetected) {
                    console.log('🦄 Safari detected');
                }
                return safariDetected;
                //return true; //for testing purposes
            };


            const HeadingsAnimation = () => {

                // Skip all heading animations on Safari
                if (isSafari()) {
                    return;
                }

                const animatedHeadings = document.querySelectorAll('.animated-heading');

                animatedHeadings.forEach((heading) => {

                    let type = heading.getAttribute('data-gsap-type') ?? 'lines'
                    
                    // Skip character and word animations on Safari for performance
                    if (isSafari() && (type === 'chars' || type === 'words')) {
                        // Simple fade-in animation for Safari instead of split text
                        const delay = heading.getAttribute('data-gsap-delay') ?? 0;
                        const startVal = heading.getAttribute('data-gsap-start') ?? 'top 90%';
                        const endVal = heading.getAttribute('data-gsap-end') ?? 'bottom center';
                        const duration = heading.getAttribute('data-gsap-duration') ?? 0.8;
                        
                        gsap.fromTo(heading, 
                            {
                                opacity: 0,
                                y: 20,
                                force3D: true
                            },
                            {
                                opacity: 1,
                                y: 0,
                                duration: duration,
                                delay: delay,
                                ease: "power3.out",
                                force3D: true,
                                willChange: 'transform, opacity',
                                scrollTrigger: {
                                    trigger: heading,
                                    markers: false,
                                    start: startVal,
                                    end: endVal,
                                    scrub: false
                                }
                            }
                        );
                        return; // Skip the split text animation
                    }

                    // Regular split text animation for non-Safari browsers
                    const splitText = new SplitText(heading, { type: 'words, lines, chars' });

                    // Store attribute values as strings first
                    const stagger = heading.getAttribute('data-gsap-stagger') ?? 0.07;
                    const delay = heading.getAttribute('data-gsap-delay') ?? 0;

                    let startVal = heading.getAttribute('data-gsap-start') ?? 'top 90%'
                    let endVal = heading.getAttribute('data-gsap-end') ?? 'bottom center'
                    let duration = heading.getAttribute('data-gsap-duration') ?? 0.8
                    let elements = splitText.words;
                    let distance = heading.getAttribute('data-gsap-distance') ?? 37 
                    let initialScale = heading.getAttribute('data-gsap-initial-scale') ?? 1

                    if(type === 'lines'){
                        elements = splitText.lines;
                    } else if(type === 'chars'){
                        elements = splitText.chars;
                    }

                    // Add hardware acceleration to all split elements
                    gsap.set(elements, { 
                        opacity: 0, 
                        scale: 0.7,
                        force3D: true,
                        backfaceVisibility: 'hidden',
                        perspective: 1000,
                        transformStyle: 'preserve-3d'
                    });

                    gsap.fromTo(
                        elements,
                    {
                        opacity: 0,
                        y: distance,
                        scale: initialScale,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: duration,
                        stagger: stagger as any,
                        delay: delay,
                        ease: "power3.out",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: heading,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });

            }

            const ImageAnimations = () => {
                // Skip all image animations on Safari but make elements visible
                if (isSafari()) {
                    const images = document.querySelectorAll('.image-parallax');
                    images.forEach(image => {
                        (image as HTMLElement).style.opacity = '1';
                    });
                    return;
                }

                const images = document.querySelectorAll('.image-parallax');

                images.forEach((image) => {

                    let startVal = image.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = image.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = image.getAttribute('data-gsap-delay') ?? 0

                    gsap.fromTo(
                        image,
                    {
                        opacity: 0,
                        y: 40,
                        scale: 0.8,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.4,
                        delay: delay,
                        ease: "easeInOut",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: image,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }

            const fadeIns = () => {
                // Skip all fade-in animations on Safari but make elements visible
                if (isSafari()) {
                    const elements = document.querySelectorAll('[data-gsap="fadeIn"]');
                    elements.forEach(element => {
                        (element as HTMLElement).style.opacity = '1';
                    });
                    return;
                }

                let elements = document.querySelectorAll('[data-gsap="fadeIn"]');

                elements.forEach((element) => {

                    let startVal = element.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = element.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = element.getAttribute('data-gsap-delay') ?? 0
                    let initialScale = element.getAttribute('data-gsap-initial-scale') ?? 0.5
                    let duration = element.getAttribute('data-gsap-duration') ?? 0.6
                    let ease = element.getAttribute('data-gsap-ease') ?? 'power2.out'

                    gsap.fromTo(
                        element,
                    {
                        opacity: 0,
                        // scale: initialScale
                    },
                    {
                        opacity: 1,
                        // scale:'1',
                        duration: duration,
                        delay: delay,
                        ease: ease,

                        scrollTrigger: {
                            trigger: element,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }
            const fadeInRight = () => {
                // Skip all fade-in-right animations on Safari but make elements visible
                if (isSafari()) {
                    const elements = document.querySelectorAll('[data-gsap="fadeInRight"]');
                    elements.forEach(element => {
                        (element as HTMLElement).style.opacity = '1';
                    });
                    return;
                }

            let elements = document.querySelectorAll('[data-gsap="fadeInRight"]');

                elements.forEach((element) => {

                    let startVal = element.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = element.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = element.getAttribute('data-gsap-delay') ?? 0
                    let duration = element.getAttribute('data-gsap-duration') ?? 0.6
                    let ease = element.getAttribute('data-gsap-ease') ?? 'power3.out'
                    let distance = element.getAttribute('data-gsap-distance') ?? 50
                    let stagger = element.getAttribute('data-gsap-stagger') ?? 0.1
                    gsap.fromTo(
                        element,
                    {
                        opacity: 0,
                        x: distance,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        x: 0,
                        duration: duration,
                        stagger: stagger as any,
                        delay: delay,
                        ease: ease,
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: element,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }
            const cardPopups = () => {
                // Skip all card popup animations on Safari but make elements visible
                if (isSafari()) {
                    const cards = document.querySelectorAll('[data-gsap="cardPopin"]');
                    cards.forEach(card => {
                        (card as HTMLElement).style.opacity = '1';
                    });
                    return;
                }

                let cards = document.querySelectorAll('[data-gsap="cardPopin"]');

                cards.forEach((card) => {
                    let startVal = card.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = card.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = card.getAttribute('data-gsap-delay') ?? 0
                    let initialDistance = card.getAttribute('data-gsap-initial-distance') ?? 50
                    let duration = card.getAttribute('data-gsap-duration') ?? 0.6

                    gsap.fromTo(
                        card,
                    {
                        opacity: 0,
                        y: initialDistance,
                        scale: 0.95,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: duration,
                        delay: delay,
                        ease: "power3.out",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: card,
                            markers: true,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }

            const divPopins = () => {

                // Find all elements with data-gsap="divPopin"
                let elements = document.querySelectorAll('[data-gsap="divPopin"]');
                
                // Skip all div popup animations on Safari but make elements visible
                if (isSafari()) {
                    const elements = document.querySelectorAll('[data-gsap="divPopin"]');
                    elements.forEach(element => {
                        (element as HTMLElement).style.opacity = '1';
                    });
                    return;
                }
                
                // Group elements by their parent container
                const groupedElements = Array.from(elements).reduce((groups, element) => {
                    const parent = element.parentElement;
                    if (!parent) return groups;
                    
                    const parentId = parent.id || parent.className;
                    if (!groups[parentId]) {
                        groups[parentId] = [];
                    }
                    groups[parentId].push(element);
                    return groups;
                }, {} as Record<string, Element[]>);

                // Animate each group
                Object.values(groupedElements).forEach(group => {
                    if (group.length > 0) {
                        const firstElement = group[0];
                        const parent = firstElement.parentElement;
                        
                        let startVal = firstElement.getAttribute('data-gsap-start') ?? 'top 85%'
                        let endVal = firstElement.getAttribute('data-gsap-end') ?? 'bottom center'
                        let delay = firstElement.getAttribute('data-gsap-delay') ?? 0
                        let initialDistance = firstElement.getAttribute('data-gsap-initial-distance') ?? 37
                        let stagger = firstElement.getAttribute('data-gsap-stagger') ?? 0.15

                        gsap.fromTo(
                            group,
                        {
                            opacity: 0,
                            y: initialDistance,
                            force3D: true
                        },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.6,
                            stagger: stagger as any,
                            delay: delay,
                            ease: "power3.out",
                            force3D: true,
                            willChange: 'transform, opacity',

                            scrollTrigger: {
                                trigger: parent,
                                markers: false,
                                start: startVal,
                                end: endVal,
                                scrub: false
                            }
                        });
                    }
                });
            }
            

            const TextAnimations = () => {
                // Skip all text animations on Safari but make elements visible
                if (isSafari()) {
                    const elements = document.querySelectorAll('[data-gsap="fromStartEnd"]');
                    elements.forEach(element => {
                        (element as HTMLElement).style.opacity = '1';
                    });
                    return;
                }

                let elements = document.querySelectorAll('[data-gsap="fromStartEnd"]');

                elements.forEach((element) => {

                    let startVal = element.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = element.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = element.getAttribute('data-gsap-delay') ?? 0

                    gsap.fromTo(
                        element,
                    {
                        opacity: 0,
                        y: 40,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        delay: delay,
                        ease: "easeOut",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: element,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }

            const imageParallax = (box: Element, scrollTriggerInstance: gsap.plugins.ScrollTriggerInstance) => {
                // Skip all image parallax effects on Safari
                if (isSafari()) {
                    return;
                }

                const viewport = ref<string>('');
                const position = ref<string>('');
                const img = box.querySelector('img');

                const startPosition = scrollTriggerInstance.start;
                const endPosition = scrollTriggerInstance.end;
                const currentPosition = ScrollTrigger.positionInViewport(box, 'top');

                const translateValue = gsap.utils.interpolate(-5, 0, currentPosition);

                const boxElement = box as HTMLElement;
                const boxHeight = boxElement.clientHeight / window.innerHeight * 100; // Convert box height to vh

                boxElement.style.height = `calc(${boxHeight}vh)`;

                if (img) {
                    img.style.height = `calc(${boxHeight}vh + 8vh)`;
                    img.style.transform = `translate3d(0px, ${translateValue}vh, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)`;
                }

                viewport.value = ScrollTrigger.isInViewport(box).toString();
                position.value = currentPosition.toFixed(2);

            };

            const updateAnimations = () => {
                HeadingsAnimation();
                ImageAnimations();
                TextAnimations();
                fadeIns();
                divPopins();
                cardPopups();
                fadeInRight();

                // Skip image parallax ScrollTrigger setup on Safari
                if (!isSafari()) {
                    const images_cards = document.querySelectorAll(".image-parallax");

                    images_cards.forEach((box) => {

                        const scrollTriggerInstance = ScrollTrigger.create({
                            trigger: box,
                            start: "top 160% bottom",
                            end: "bottom top",
                            // markers: true,
                            onUpdate: () => imageParallax(box, scrollTriggerInstance),
                        });

                        imageParallax(box, scrollTriggerInstance);
                    });
                }
            };

            const isTransitioning = ref(false);
            const isFooterTransitioning = ref(true);

            const onAfterEnter = () => {
                // SAFARI OPTIMIZED: Use requestAnimationFrame for smooth transitions
                requestAnimationFrame(() => {
                    isTransitioning.value = false;
                    
                    // Throttled scroll reset for Safari
                    if (window.requestIdleCallback) {
                        window.requestIdleCallback(() => {
                            window.scrollTo(0, 0);
                        });
                    } else {
                        setTimeout(() => {
                            window.scrollTo(0, 0);
                        }, 16); // ~1 frame delay
                    }
                });
                
                console.log('🔍 Safari-optimized transitions enabled');
            };

            const onBeforeLeave = () => {
                // SAFARI OPTIMIZED: Simplified state management
                requestAnimationFrame(() => {
                    isTransitioning.value = true;
                });
                console.log('🔍 Safari-optimized transition starting');
            };

            const onBeforeEnter = () => {
                // DEBUGGING: Temporarily disable footer transition state
                // isFooterTransitioning.value = true;
                console.log('🔍 onBeforeEnter disabled for debugging');
            };

            const onFooterAfterLeave = () => {
                // SAFARI OPTIMIZED: Minimal footer transition logic
                console.log('🔍 Safari-optimized footer transition complete');
            };

            const onFooterBeforeEnter = () => {
                // SAFARI OPTIMIZED: Minimal footer transition logic
                console.log('🔍 Safari-optimized footer transition');
            };

            onMounted(async () => {
                // DEBUGGING: Temporarily disable UserWay widget loading to test Safari performance
                // // Load UserWay accessibility widget
                // try {
                //     await loadUserwayConfig('en');
                // } catch (error) {
                //     // UserWay failed to load, but app should continue normally
                // }
                
                console.log('🔍 UserWay widget loading disabled for debugging');
            });

            // Setup global error handling
            onMounted(() => {
                setupGlobalErrorHandler();
            });

            const handleGlobalError = (errorData: any) => {
                console.error('Global error caught:', errorData);
                // Additional error reporting could be added here
            };

            const handleRetry = () => {
                console.log('Retrying operation...');
                // Force a page refresh as fallback
                window.location.reload();
            };

            return {
                showPopup,
                onClosePopup,
                isTransitioning,
                onAfterEnter,
                onBeforeLeave,
                onFooterBeforeEnter,
                onFooterAfterLeave,
                onBeforeEnter,
                handleGlobalError,
                handleRetry,
                isSafari
            };
        },
    })

</script>

<style>
.up-enter-from, .up-leave-to{
    opacity: 0;
    transition: opacity 0.4s ease-in-out;
}
.up-enter-active, .up-leave-active{
    transition: opacity 0.4s ease-in-out;
    /* transition: opacity 0.4s ease-in-out; */
}
/* .footer-enter-from, .footer-leave-to{
    visibility: hidden;
}
.footer-enter-active, .footer-leave-active{
    visibility: hidden;
} */
</style>
